// ─── SponsorsPanel.jsx ───────────────────────────────────────────────────────
import React, { useState } from "react";
import { supabase } from "../../../supabaseClient";
import { useAdmin } from "../../../context/AdminContext";
import { useSeason } from "../../../context/SeasonContext";
import { InlineNotice } from "../../../utils/InlineNotice";
import { useInlineNotice } from "../../../hooks/useInlineNotice";
import { InlineConfirm } from "../../../utils/InlineConfirm";

export const SponsorsPanel = () => {
  const { adminId, activeSeason } = useAdmin();
  const { sponsors, loadSponsors } = useSeason();

  const [newSponsor, setNewSponsor] = useState({ name: "", website_url: "" });
  const [sponsorLogoUploading, setSponsorLogoUploading] = useState({});
  const { notice, showError, clear } = useInlineNotice();

  const addSponsor = async () => {
    clear();
    if (!newSponsor.name.trim()) {
      showError("Sponsor name required");
      return;
    }
    if (!activeSeason?.id) {
      showError("No season selected");
      return;
    }
    try {
      await supabase.functions.invoke("admin-actions", {
        body: {
          action: "addSponsor",
          adminId,
          payload: { ...newSponsor, season_id: activeSeason.id },
        },
      });
      setNewSponsor({ name: "", website_url: "" });
      await loadSponsors(activeSeason.id);
    } catch (err) {
      showError(`Error: ${err.message}`);
    }
  };

  const deleteSponsor = async (id) => {
    try {
      await supabase.functions.invoke("admin-actions", {
        body: { action: "deleteSponsor", adminId, payload: { sponsorId: id } },
      });
      if (activeSeason?.id) {
        await loadSponsors(activeSeason.id);
      }
    } catch (err) {
      showError(`Error: ${err.message}`);
    }
  };

  const uploadSponsorLogo = async (sponsorId, file) => {
    setSponsorLogoUploading((prev) => ({ ...prev, [sponsorId]: true }));
    const ext = file.name.split(".").pop();
    const fileName = `sponsor-${sponsorId}-${Date.now()}.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from("sponsor-logos")
      .upload(fileName, file);
    if (uploadError) {
      showError(`Upload failed: ${uploadError.message}`);
      setSponsorLogoUploading((prev) => ({ ...prev, [sponsorId]: false }));
      return;
    }

    const {
      data: { publicUrl },
    } = supabase.storage.from("sponsor-logos").getPublicUrl(fileName);

    try {
      await supabase.functions.invoke("admin-actions", {
        body: JSON.stringify({
          action: "updateSponsorLogo",
          adminId,
          payload: { sponsorId, logoUrl: publicUrl },
        }),
        headers: { "Content-Type": "application/json" },
      });
      if (activeSeason?.id) {
        await loadSponsors(activeSeason.id);
      }
    } catch (err) {
      showError(`Error saving logo: ${err.message}`);
    }
    setSponsorLogoUploading((prev) => ({ ...prev, [sponsorId]: false }));
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
        Manage Sponsors
      </h2>

      <InlineNotice notice={notice} onDismiss={clear} />

      <div className="border dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700/50">
        <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
          Add Sponsor
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <input
            type="text"
            placeholder="Sponsor Name"
            value={newSponsor.name}
            onChange={(e) =>
              setNewSponsor({ ...newSponsor, name: e.target.value })
            }
            className="border dark:border-gray-500 p-2 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
          <input
            type="text"
            placeholder="Website URL (optional)"
            value={newSponsor.website_url}
            onChange={(e) =>
              setNewSponsor({ ...newSponsor, website_url: e.target.value })
            }
            className="border dark:border-gray-500 p-2 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
          <button
            onClick={addSponsor}
            className="bg-orange-600 text-white px-6 py-2 rounded hover:bg-orange-700"
          >
            + Add Sponsor
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {sponsors.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400 text-center py-8">
            No sponsors yet.
          </p>
        ) : (
          sponsors.map((sponsor) => (
            <div
              key={sponsor.id}
              className="border dark:border-gray-600 rounded-lg p-4 bg-white dark:bg-gray-800 flex items-center gap-4"
            >
              <div className="w-16 h-16 flex-shrink-0 border dark:border-gray-600 rounded overflow-hidden">
                {sponsor.logo_url ? (
                  <img
                    src={sponsor.logo_url}
                    alt={sponsor.name}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-3xl">
                    🏢
                  </div>
                )}
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-900 dark:text-white">
                  {sponsor.name}
                </p>
                {sponsor.website_url && (
                  <a
                    href={sponsor.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 dark:text-blue-400"
                  >
                    🔗 {sponsor.website_url}
                  </a>
                )}
              </div>
              <div className="flex items-center gap-2">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) =>
                      e.target.files?.[0] &&
                      uploadSponsorLogo(sponsor.id, e.target.files[0])
                    }
                  />
                  <span className="text-xs bg-gray-200 dark:bg-gray-700 px-3 py-1 rounded hover:bg-gray-300 dark:hover:bg-gray-600">
                    {sponsorLogoUploading[sponsor.id]
                      ? "Uploading…"
                      : "📸 Change Logo"}
                  </span>
                </label>
                <InlineConfirm
                  triggerLabel="Delete"
                  triggerClassName="text-red-600 dark:text-red-400 text-sm hover:underline"
                  message={`Delete sponsor "${sponsor.name}"?`}
                  confirmLabel="Delete"
                  onConfirm={() => deleteSponsor(sponsor.id)}
                />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
